import { useParams } from "react-router-dom";
import ApiStatus from "../apiStatus";
import { useDeleteCapability, useFetchCapability, useUpdateCapability } from "../hooks/CapabilityHooks";
import ValidationSummary from "../ValidationSummary";
import CapabilityForm from "./CapabilityForm";
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useContext, useState } from "react";
import { Modal, Button } from 'react-bootstrap';
import { AuthContext } from "../AuthContext";
import { CapabilityDTO } from "../types/capabilityDTO";

const CapabilityEdit = () => {
  const { t } = useTranslation();
  const { roles } = useContext(AuthContext);
  const { organisationId, capabilityId } = useParams<{ organisationId: string, capabilityId: string }>();
  if (!organisationId) throw Error("Need a organisation id");
  if (!capabilityId) throw Error("Need a capability id");

  const { data, status, isSuccess } = useFetchCapability(organisationId, capabilityId);
  const updateCapabilityMutation = useUpdateCapability(organisationId);
  const deleteCapabilityMutation = useDeleteCapability(organisationId);
  const [capabilityToDelete, setCapabilityToDelete] = useState<CapabilityDTO | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const canDelete = roles.includes("SysAdmin") || roles.includes("OptiviAdmin");

  const handleCapabilityDelete = (capability: CapabilityDTO) => {
    setCapabilityToDelete(capability);
    setShowDeleteModal(true);
  };

  const handleDelete = () => {
    if (!capabilityToDelete) return;

    deleteCapabilityMutation.mutate(capabilityToDelete, {
      onSuccess: () => {
        setShowDeleteModal(false);
        toast.success(t('capability.deleteSuccess'));
        history.back();
      },
      onError: () => {
        setShowDeleteModal(false);
        toast.error(t('capability.deleteError'));
      }
    });
  };

  if (!isSuccess) return <ApiStatus status={status} />;

  const capabilityWithLocalDates = {
    ...data,
    validFrom: new Date(data.validFrom + 'Z'),
    validTo: new Date(data.validTo + 'Z'),
    lastModified: new Date(data.lastModified + 'Z')
  }

  return (
    <>
      {updateCapabilityMutation.isError && (
        <ValidationSummary error={updateCapabilityMutation.error} />
      )}
      {updateCapabilityMutation.isPending ? (
        <div className="spinner-container" style={{ textAlign: 'center', marginTop: '20px' }}>
          <ClipLoader size={50} color={"#123abc"} loading={true} />
          <p>{t('capability.editWait')}</p>
        </div>
      ) : (
        <CapabilityForm
          headerText={t('capability.edit')}
          capability={capabilityWithLocalDates}
          submitted={(capability) => {
            updateCapabilityMutation.mutate(capability, {
              onSuccess: () => {
                toast.success(t('capability.editSuccess'));
                history.back();
              },
              onError: () => {
                toast.error(t('capability.editError'));
              }
            });
          }}
          deleted={canDelete ? handleCapabilityDelete : undefined}
        />
      )}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('common.confirmation')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('capability.deleteConfirmation')}</p>
          {capabilityToDelete && (
            <div><strong>{t('common.name')}:</strong> {capabilityToDelete.name}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleDelete} disabled={deleteCapabilityMutation.isPending}>
            {t('buttons.delete')}
          </Button>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteCapabilityMutation.isPending}>
            {t('buttons.cancel')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CapabilityEdit;
